import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CutAddon, CutType, CutPhase } from '@busy/core';
import { logCutTimerAbandon, type CutQuality } from '@busy/api';

/** Crop rectangle for the high-fidelity native-source compose path.
 *  Coordinates are in the NATIVE pixel space of the source camera
 *  blob (NOT scaled or normalized). The composite reads these
 *  directly into a canvas drawImage call so the rendered region
 *  preserves the camera's full resolution. */
export type PhotoCropArea = { x: number; y: number; width: number; height: number };

/* ============================================================================
 * Cut Timer state machine
 *
 * Modes (the canonical flow):
 *   idle     — modal closed, no active cut
 *   chair    — Today's chair: day-at-a-glance + "Start new cut" entry
 *   checkin  — picking the client (or walk-in)
 *   picker   — picking the service / cut type (client already chosen)
 *   running  — live timer screen (paused or ticking)
 *   summary  — post-cut wrap (price, save-to-client, save visit)
 *
 * Persisted to localStorage so a refresh during an active cut resumes correctly.
 * Stale-timer protection: cuts running > 4 hrs auto-end at target on next open
 * (handled in the entry handler, not the store itself).
 *
 * Per-phase timing model (mirrors the demo):
 *   - phaseElapsedSec[i] holds COMMITTED seconds for phase i (frozen on switch).
 *   - Live ticking phase = currentPhaseIdx. Live elapsed is computed:
 *       now - phaseStartedAt - phaseAccumPausedMs - currentPauseElapsed
 *     and added on top of phaseElapsedSec[currentPhaseIdx] for display.
 *   - On phase switch, we commit the live time to phaseElapsedSec[idx] and
 *     reset phaseStartedAt / phaseAccumPausedMs for the new phase.
 *   - Pause adds to phaseAccumPausedMs on resume.
 * ============================================================================ */

export type Mode =
  | 'idle'
  | 'chair'
  | 'checkin'
  | 'picker'
  | 'before-photo' // optional capture step between picker and running
  | 'running'
  | 'rating'      // optional quality rating step between running and after-photo
  | 'after-photo' // optional capture step between rating and summary
  | 'reveal'      // post-after composite/video reveal — share + save + continue
  | 'summary';

// Stale-timer protection thresholds (mirrors the demo's STALE_TIMER_THRESHOLD_MS / LONG_CUT_RATIO).
//   STALE: > 4 hrs running → auto-finalize at target durations on next openChair
//   LONG_CUT_RATIO: elapsed > 1.5× target → confirmation in the post-cut summary
export const STALE_TIMER_THRESHOLD_MS = 4 * 60 * 60 * 1000;
export const LONG_CUT_RATIO = 1.5;

export type CheckedInClient =
  | { kind: 'existing'; id: string; firstName: string; lastName: string; phone: string | null }
  | { kind: 'walkin' };

export type AddedAddonEntry = {
  id: string;
  name: string;
  // i18n key for display — falls back to `name` when missing.
  nameKey?: string;
  priceCents: number;
  // Index of the first phase this addon contributed to the phases[] array.
  // Used to "lock" removal once the cut has reached/passed those phases.
  // For single-phase cuts (where the addon is folded into the single phase
  // instead of appended), startIdx points at the single phase index (0)
  // and phaseCount is 0 — see foldedSec below for the seconds we owe back
  // on remove.
  startIdx: number;
  phaseCount: number;
  // Set when the addon was folded into a single-phase cut's target rather
  // than appended as new phases. Stores the seconds we added so removal
  // can subtract the right amount. Undefined for multi-phase cuts.
  foldedSec?: number;
};

type CutTimerState = {
  mode: Mode;

  // Pending state held during the multi-step flow (chair → checkin → picker → running).
  // Cleared on resetAll and on closing the modal from a non-running state.
  pendingClient: CheckedInClient | null; // set by checkin, consumed by startCut
  pendingCutTypeId: string | null; // set by picker, consumed by startCut
  // Walk-in vs appointment flag set at CHECK-IN (vs the legacy post-cut
  // capture in PostCutSummary). Pre-cut capture gets a much higher
  // hit-rate — barber is already paying attention to who's in the
  // chair, so the toggle is two taps deep into a flow they're already
  // executing. PostCutSummary still surfaces the picker as a final
  // chance to correct, seeded from this value. Null = unset.
  pendingWasAppointment: boolean | null;
  // Quality rating (1=off, 2=solid, 3=nailed) captured at the dedicated
  // RATING step between endCut and after-photo — that's the moment of
  // peak attention (barber just finished, judgment is fresh, client
  // still in chair). PostCutSummary still surfaces the picker for
  // final correction, seeded from this value. Null = unset.
  pendingQuality: CutQuality | null;

  // Active cut state (populated on start, cleared on dismiss/save)
  cutType: CutType | null; // deep-clone of the catalog entry, mutated by addAddon/removeAddon
  client: CheckedInClient | null;
  currentPhaseIdx: number;
  phaseElapsedSec: number[]; // committed seconds per phase
  phaseStartedAt: number | null; // ms timestamp; null when not running yet
  phaseAccumPausedMs: number;
  pausedAt: number | null; // ms timestamp; non-null = currently paused
  paused: boolean;
  // Session-wide pause analytics (migration 0075). Independent of
  // phaseAccumPausedMs (which is per-phase and resets on phase change).
  // Persisted to visits.paused_seconds + visits.pause_count at save.
  sessionPausedMs: number;
  sessionPauseCount: number;
  // Count of phases the barber skipped via the Skip Phase button.
  // Persisted to visits.phases_skipped at save.
  sessionPhasesSkipped: number;
  addedAddons: AddedAddonEntry[];
  sessionId: string | null;
  startedAtIso: string | null; // for visit logging — wall-clock cut start
  endedAtIso: string | null; // set by endCut, used by summary

  // Tip rotation
  lastTipCat: string | null;
  tipCycleIdx: number;

  // True when the cut was auto-finalized by stale-timer protection (> 4 hrs).
  // PostCutSummary reads this to show a "auto-ended" banner. Reset by resetAll.
  staleAutoEnded: boolean;

  // Forces re-render every tick when the timer is ticking
  tickCount: number;

  // Before & After photos. Held as in-memory Blobs + object URLs so the
  // LiveTimer can render a small thumbnail nudge and the PostCutSummary
  // can compose the side-by-side. NOT persisted — Blobs don't serialize
  // through localStorage. If the tab is killed mid-cut, photos are lost
  // (uploads happen at summary save time).
  beforePhotoBlob: Blob | null;
  beforePhotoUrl: string | null;
  afterPhotoBlob: Blob | null;
  afterPhotoUrl: string | null;

  // High-fidelity native sources for the SHARE composite path. The
  // photoBlob fields above are cropped + recompressed (1024px JPEG)
  // for storage upload + thumbnail display. These hold the ORIGINAL
  // camera File/Blob + the crop area the user selected so the
  // transformation composite can render at native resolution in a
  // single canvas pass — no double JPEG compression, no upsampling
  // from 1024 to 1080. Captures backward-compatible: a caller that
  // doesn't populate these gets the legacy two-pass behavior. Only
  // the cut timer's before/after flow currently sets them.
  //
  // Cleared with the photo blobs on resetAll / discardAfter. Memory
  // cost is ~3-5 MB per source blob (native iPhone JPEG) — held
  // only for the cut's duration (~20-60 min) so unproblematic.
  beforePhotoSource: Blob | null;
  beforePhotoCrop: PhotoCropArea | null;
  afterPhotoSource: Blob | null;
  afterPhotoCrop: PhotoCropArea | null;

  // Optional video clips, kept alongside the still photos. When the barber
  // recorded a clip in BeforePhotoStep / AfterPhotoStep, the trimmed video
  // Blob is stashed here so the Transformation Reveal step can compose it
  // into the simultaneous 9:16 stack. Path 1 ephemeral: never uploaded.
  beforeVideoBlob: Blob | null;
  afterVideoBlob: Blob | null;
  // Optional pan/zoom crop rect captured AFTER recording — lets the
  // barber reframe the clip inside the picture-frame window without
  // having to retake. Coords are in native video pixels (matches
  // PhotoCropArea); videoStitch.ts uses them as the source rect when
  // drawing each frame. When null (rare — no crop step ran), stitch
  // falls back to the full-video cover-fit behavior.
  beforeVideoCrop: PhotoCropArea | null;
  afterVideoCrop: PhotoCropArea | null;

  // Set true the first time the reveal step finishes producing the
  // transformation video. Read at visit-commit time to award the
  // Transformations achievement count for this cut.
  transformationGenerated: boolean;

  // Actions — flow control
  openChair: () => void;
  resumeRunningCut: () => void; // chair → running (resume banner)
  startNewCut: () => void; // chair → checkin (clears any leftover pending state)
  pickClient: (client: CheckedInClient) => void; // checkin → picker
  setPendingWasAppointment: (v: boolean | null) => void;
  setPendingQuality: (v: CutQuality | null) => void;
  goToAfterPhotoFromRating: () => void; // rating → after-photo
  backToCheckin: () => void; // picker → checkin (re-pick client)
  pickCutType: (cutType: CutType) => void; // picker → before-photo
  // Photo flow actions
  setBeforePhoto: (blob: Blob, source?: Blob, crop?: PhotoCropArea) => void;
  setAfterPhoto: (blob: Blob, source?: Blob, crop?: PhotoCropArea) => void;
  setBeforeVideo: (blob: Blob | null, crop?: PhotoCropArea | null) => void;
  setAfterVideo: (blob: Blob | null, crop?: PhotoCropArea | null) => void;
  markTransformationGenerated: () => void;
  clearAfterPhoto: () => void;
  startCutFromBefore: () => void; // before-photo → running (start the actual cut session)
  backToPicker: () => void; // before-photo → picker
  goToRevealFromAfter: () => void; // after-photo → reveal
  goToSummaryFromReveal: () => void; // reveal → summary
  goToSummaryFromAfter: () => void; // after-photo → summary (bypass reveal)
  // Active-cut actions
  switchToPhase: (idx: number) => void;
  pause: () => void;
  resume: () => void;
  addAddon: (addon: CutAddon) => void;
  removeAddon: (addonId: string) => void;
  endCut: () => void;
  // Summary-stage action: retroactively attach a walk-in to a known client
  attachClient: (client: CheckedInClient) => void;
  // Stale-timer fallback: replace phase elapsed with phase targets
  useTargetTimeAsElapsed: () => void;
  closeModal: () => void;
  resetAll: () => void;
  tick: () => void;
};

// Releases the object URLs held for before/after photo previews. Called
// any time we're about to drop the photo references (close / reset / discard)
// so blob memory doesn't leak across sessions.
function revokePhotoUrls(s: { beforePhotoUrl: string | null; afterPhotoUrl: string | null }) {
  if (s.beforePhotoUrl) URL.revokeObjectURL(s.beforePhotoUrl);
  if (s.afterPhotoUrl) URL.revokeObjectURL(s.afterPhotoUrl);
}

/** Pull the client id out of a CheckedInClient — null for walk-ins or
 *  when there's no client at all. Shared by abandon-logging paths so
 *  the cut_timer_abandons.client_id column reflects who was picked
 *  (when anyone was). */
function clientIdFor(c: CheckedInClient | null): string | null {
  if (c == null) return null;
  if (c.kind === 'existing') return c.id;
  return null;
}

function freshActiveState() {
  return {
    pendingClient: null as CheckedInClient | null,
    pendingCutTypeId: null as string | null,
    pendingWasAppointment: null as boolean | null,
    pendingQuality: null as CutQuality | null,
    cutType: null as CutType | null,
    client: null as CheckedInClient | null,
    currentPhaseIdx: 0,
    phaseElapsedSec: [] as number[],
    phaseStartedAt: null as number | null,
    phaseAccumPausedMs: 0,
    pausedAt: null as number | null,
    paused: false,
    sessionPausedMs: 0,
    sessionPauseCount: 0,
    sessionPhasesSkipped: 0,
    addedAddons: [] as AddedAddonEntry[],
    sessionId: null as string | null,
    startedAtIso: null as string | null,
    endedAtIso: null as string | null,
    lastTipCat: null as string | null,
    tipCycleIdx: -1,
    staleAutoEnded: false,
    tickCount: 0,
    beforePhotoBlob: null as Blob | null,
    beforePhotoUrl: null as string | null,
    afterPhotoBlob: null as Blob | null,
    afterPhotoUrl: null as string | null,
    // High-fidelity native sources — see comment on the State type
    // above for rationale. Held in memory only; never uploaded.
    beforePhotoSource: null as Blob | null,
    beforePhotoCrop: null as PhotoCropArea | null,
    afterPhotoSource: null as Blob | null,
    afterPhotoCrop: null as PhotoCropArea | null,
    beforeVideoBlob: null as Blob | null,
    afterVideoBlob: null as Blob | null,
    beforeVideoCrop: null as PhotoCropArea | null,
    afterVideoCrop: null as PhotoCropArea | null,
    transformationGenerated: false,
  };
}

export const useCutTimer = create<CutTimerState>()(
  persist(
    (set, get) => ({
      mode: 'idle',
      ...freshActiveState(),

      // Entry: tap CUT button → always lands on Today's Chair.
      // The chair view itself shows the active-cut resume banner if a cut is running.
      //
      // Stale-timer protection (Layer 2): if there's an active cut that's been running
      // for > 4 hours, auto-finalize it at target durations and route to summary so
      // the barber gets a chance to log it (or discard) without polluting analytics.
      openChair: () => {
        const s = get();
        if (s.cutType && s.startedAtIso) {
          const ageMs = Date.now() - new Date(s.startedAtIso).getTime();
          if (ageMs > STALE_TIMER_THRESHOLD_MS) {
            // Stale auto-end is a soft abandon — the timer ran past the
            // sanity threshold without the user wrapping up, so we
            // force-finalize at target durations. Log it as its own
            // 'auto_stale' phase so the analytics can separate this
            // (background friction / forgot-to-end) from explicit
            // discards in the summary screen.
            void logCutTimerAbandon({
              phase: 'auto_stale',
              clientId: clientIdFor(s.client),
              cutTypeName: s.cutType.name,
              startedAt: s.startedAtIso,
              timerStarted: true,
            });
            // Replace all phase elapsed with their target durations and route to summary.
            set({
              mode: 'summary',
              phaseElapsedSec: s.cutType.phases.map((p) => p.sec),
              endedAtIso: new Date().toISOString(),
              paused: true,
              pausedAt: null,
              staleAutoEnded: true,
            });
            return;
          }
        }
        set({ mode: 'chair', pendingClient: null, pendingCutTypeId: null, pendingWasAppointment: null, pendingQuality: null });
      },

      // Resume a minimized cut from the chair view (resume banner click).
      // If the cut already ended (endedAtIso is set), the barber minimized
      // mid-after-photo or somewhere between end and save — restore to the
      // after-photo step so they can finish capturing / sharing / saving.
      resumeRunningCut: () => {
        const s = get();
        const ended = !!s.endedAtIso;
        set({ mode: ended ? 'after-photo' : 'running' });
      },

      // Start a new cut. If a cut is currently active, the chair view should show
      // the switch-clients confirmation BEFORE calling this. This action assumes
      // the caller has already handled (or chosen to override) the active cut.
      startNewCut: () =>
        set({ mode: 'checkin', pendingClient: null, pendingCutTypeId: null, pendingWasAppointment: null, pendingQuality: null }),

      pickClient: (client: CheckedInClient) =>
        set({ mode: 'picker', pendingClient: client }),

      setPendingWasAppointment: (v: boolean | null) => {
        set({ pendingWasAppointment: v });
      },

      setPendingQuality: (v: CutQuality | null) => {
        set({ pendingQuality: v });
      },

      // Rating step → after-photo. Fired from RatingStep's Continue
      // button. Skipping (continuing with quality=null) is fine — the
      // post-cut summary surfaces the picker again as a safety net.
      goToAfterPhotoFromRating: () => {
        set({ mode: 'after-photo' });
      },

      backToCheckin: () => set({ mode: 'checkin' }),

      pickCutType: (cutType: CutType) => {
        const s = get();
        if (!s.pendingClient) return; // shouldn't happen — picker is only reachable after checkin
        // Deep-clone so addons can mutate phases without polluting the catalog.
        const cloned: CutType = JSON.parse(JSON.stringify(cutType));
        // Stage the pick for the before-photo step. The cut session itself
        // doesn't start until the barber confirms or skips the before photo
        // and startCutFromBefore() runs.
        set({
          mode: 'before-photo',
          cutType: cloned,
          pendingCutTypeId: cloned.id,
        });
      },

      // --- Before / After photo flow ---

      // Photo setters now optionally accept the high-fidelity native
      // source + crop area. When provided, the share composite reads
      // FROM the native source (drawImage with the crop region) for a
      // single-canvas-pass render — avoids the otherwise two-pass JPEG
      // compression that softens fade detail. The cropped `blob` is
      // still what gets uploaded to Supabase (small, fast) and
      // displayed in the preview thumbnails.
      setBeforePhoto: (blob: Blob, source?: Blob, crop?: PhotoCropArea) => {
        const s = get();
        if (s.beforePhotoUrl) URL.revokeObjectURL(s.beforePhotoUrl);
        set({
          beforePhotoBlob: blob,
          beforePhotoUrl: URL.createObjectURL(blob),
          beforePhotoSource: source ?? null,
          beforePhotoCrop: crop ?? null,
        });
      },

      setAfterPhoto: (blob: Blob, source?: Blob, crop?: PhotoCropArea) => {
        const s = get();
        if (s.afterPhotoUrl) URL.revokeObjectURL(s.afterPhotoUrl);
        set({
          afterPhotoBlob: blob,
          afterPhotoUrl: URL.createObjectURL(blob),
          afterPhotoSource: source ?? null,
          afterPhotoCrop: crop ?? null,
        });
      },

      setBeforeVideo: (blob: Blob | null, crop?: PhotoCropArea | null) => {
        // Clear the crop alongside the blob — re-recording a clip
        // invalidates any prior crop coords (different source, different
        // intrinsic dimensions). The crop step in VideoCaptureModal
        // rewrites both in one setter call so the store never lands in
        // a torn state.
        set({ beforeVideoBlob: blob, beforeVideoCrop: blob ? (crop ?? null) : null });
      },

      setAfterVideo: (blob: Blob | null, crop?: PhotoCropArea | null) => {
        set({ afterVideoBlob: blob, afterVideoCrop: blob ? (crop ?? null) : null });
      },

      markTransformationGenerated: () => {
        set({ transformationGenerated: true });
      },

      clearAfterPhoto: () => {
        const s = get();
        if (s.afterPhotoUrl) URL.revokeObjectURL(s.afterPhotoUrl);
        set({
          afterPhotoBlob: null,
          afterPhotoUrl: null,
          afterVideoBlob: null,
          afterVideoCrop: null,
          afterPhotoSource: null,
          afterPhotoCrop: null,
        });
      },

      // Leaves before-photo (skipped or captured) and starts the actual cut.
      // This is the work that used to live inside pickCutType — moved here
      // so the timer doesn't begin until the barber gets through (or skips)
      // the before-photo step.
      startCutFromBefore: () => {
        const s = get();
        if (!s.cutType || !s.pendingClient) return;
        const sessionId = `cut-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        set({
          mode: 'running',
          pendingClient: null,
          pendingCutTypeId: null,
          client: s.pendingClient,
          currentPhaseIdx: 0,
          phaseElapsedSec: new Array(s.cutType.phases.length).fill(0),
          phaseStartedAt: Date.now(),
          phaseAccumPausedMs: 0,
          pausedAt: null,
          paused: false,
          addedAddons: [],
          sessionId,
          startedAtIso: new Date().toISOString(),
          endedAtIso: null,
          lastTipCat: null,
          tipCycleIdx: -1,
          tickCount: 0,
        });
      },

      // before-photo → picker. Drops the staged cut type but keeps the
      // pending client, so re-picking the service is one tap.
      backToPicker: () => {
        const s = get();
        if (s.beforePhotoUrl) URL.revokeObjectURL(s.beforePhotoUrl);
        set({
          mode: 'picker',
          cutType: null,
          pendingCutTypeId: null,
          beforePhotoBlob: null,
          beforePhotoUrl: null,
        });
      },

      switchToPhase: (idx: number) => {
        const s = get();
        if (!s.cutType || idx < 0 || idx >= s.cutType.phases.length || idx === s.currentPhaseIdx) {
          return;
        }
        // Commit current phase's live time
        const live = computeLiveElapsedSec(s);
        const next = [...s.phaseElapsedSec];
        next[s.currentPhaseIdx] = (next[s.currentPhaseIdx] ?? 0) + live;
        // If we're switching to a LATER phase and the current phase
        // accumulated zero seconds of live time, the barber effectively
        // skipped it. Count toward session_phases_skipped for analytics.
        // Going backwards (idx < currentPhaseIdx) is a correction, not
        // a skip — don't count.
        const skipped =
          idx > s.currentPhaseIdx && (next[s.currentPhaseIdx] ?? 0) === 0;
        // If paused at the moment of the phase switch, flush the
        // pause-so-far to the session counter so we don't drop it
        // when pausedAt gets reset for the new phase.
        const flushedSessionPausedMs = s.paused && s.pausedAt != null
          ? s.sessionPausedMs + (Date.now() - s.pausedAt)
          : s.sessionPausedMs;
        set({
          currentPhaseIdx: idx,
          phaseElapsedSec: next,
          phaseStartedAt: Date.now(),
          phaseAccumPausedMs: 0,
          pausedAt: s.paused ? Date.now() : null,
          sessionPausedMs: flushedSessionPausedMs,
          sessionPhasesSkipped: s.sessionPhasesSkipped + (skipped ? 1 : 0),
          // Force a re-render now and reset tip cycle for the new phase
          tickCount: s.tickCount + 1,
        });
      },

      pause: () => {
        const s = get();
        if (s.paused || s.mode !== 'running') return;
        set({
          paused: true,
          pausedAt: Date.now(),
          // Increment the session pause counter on the LEADING edge of
          // each pause cycle so a pause→resume→pause→resume counts as 2.
          sessionPauseCount: s.sessionPauseCount + 1,
        });
      },

      resume: () => {
        const s = get();
        if (!s.paused || s.pausedAt == null) return;
        const delta = Date.now() - s.pausedAt;
        set({
          paused: false,
          pausedAt: null,
          phaseAccumPausedMs: s.phaseAccumPausedMs + delta,
          // Session-wide accumulator for visits.paused_seconds at save.
          sessionPausedMs: s.sessionPausedMs + delta,
        });
      },

      addAddon: (addon: CutAddon) => {
        const s = get();
        if (!s.cutType) return;
        if (s.addedAddons.some((a) => a.id === addon.id)) return;

        // Single-phase cut (use-phases toggle OFF in the editor) → fold the
        // addon's total time into the single phase's target instead of
        // appending phases. Otherwise the cut would suddenly become
        // multi-phase the moment a barber adds a hot towel, defeating the
        // single-phase choice.
        const isSinglePhase =
          s.cutType.phases.length === 1 && s.cutType.phases[0]?.cat === 'single';
        if (isSinglePhase) {
          const foldedSec = addon.phases.reduce((sum, p) => sum + p.sec, 0);
          const single = s.cutType.phases[0]!;
          const phases: CutPhase[] = [{ ...single, sec: single.sec + foldedSec }];
          const entry: AddedAddonEntry = {
            id: addon.id,
            name: addon.name,
            nameKey: addon.nameKey,
            priceCents: addon.priceCents,
            startIdx: 0,
            phaseCount: 0,
            foldedSec,
          };
          set({
            cutType: { ...s.cutType, phases },
            // phaseElapsedSec stays the same length (still 1 element)
            addedAddons: [...s.addedAddons, entry],
          });
          return;
        }

        // Multi-phase cut — append the addon's phases. They flow naturally
        // after whatever phase is currently active.
        const phases: CutPhase[] = [...s.cutType.phases, ...addon.phases];
        const startIdx = s.cutType.phases.length;
        const elapsedExt = [...s.phaseElapsedSec, ...new Array(addon.phases.length).fill(0)];
        const entry: AddedAddonEntry = {
          id: addon.id,
          name: addon.name,
          nameKey: addon.nameKey,
          priceCents: addon.priceCents,
          startIdx,
          phaseCount: addon.phases.length,
        };
        set({
          cutType: { ...s.cutType, phases },
          phaseElapsedSec: elapsedExt,
          addedAddons: [...s.addedAddons, entry],
        });
      },

      removeAddon: (addonId: string) => {
        const s = get();
        if (!s.cutType) return;
        const entry = s.addedAddons.find((a) => a.id === addonId);
        if (!entry) return;

        // Folded (single-phase) addon: subtract the seconds we added back
        // from the single phase's target. No phase array surgery needed,
        // and removal is never locked since the addon doesn't have its
        // own phase boundary to cross.
        if (entry.phaseCount === 0 && entry.foldedSec != null) {
          const single = s.cutType.phases[0];
          if (!single) return;
          const phases: CutPhase[] = [
            { ...single, sec: Math.max(0, single.sec - entry.foldedSec) },
          ];
          set({
            cutType: { ...s.cutType, phases },
            addedAddons: s.addedAddons.filter((a) => a.id !== addonId),
          });
          return;
        }

        // Locked once the cut has reached/passed the addon's phases
        if (s.currentPhaseIdx >= entry.startIdx) return;
        // Trim phases + elapsed array; shift indices of any addon that came after
        const phases = [
          ...s.cutType.phases.slice(0, entry.startIdx),
          ...s.cutType.phases.slice(entry.startIdx + entry.phaseCount),
        ];
        const elapsed = [
          ...s.phaseElapsedSec.slice(0, entry.startIdx),
          ...s.phaseElapsedSec.slice(entry.startIdx + entry.phaseCount),
        ];
        const nextAddons = s.addedAddons
          .filter((a) => a.id !== addonId)
          .map((a) => (a.startIdx > entry.startIdx ? { ...a, startIdx: a.startIdx - entry.phaseCount } : a));
        set({
          cutType: { ...s.cutType, phases },
          phaseElapsedSec: elapsed,
          addedAddons: nextAddons,
        });
      },

      attachClient: (client: CheckedInClient) => set({ client }),

      // Stale-timer protection (Layer 1): replace phase elapsed values with their
      // target durations. Called from PostCutSummary when the user chooses to log
      // at target rather than the actual (long) elapsed, OR from openChair when the
      // timer has been running > 4 hours.
      useTargetTimeAsElapsed: () => {
        const s = get();
        if (!s.cutType) return;
        set({
          phaseElapsedSec: s.cutType.phases.map((p) => p.sec),
          paused: true,
          pausedAt: null,
        });
      },

      endCut: () => {
        const s = get();
        if (!s.cutType) return;
        // Commit current phase's live time before transitioning
        const live = computeLiveElapsedSec(s);
        const next = [...s.phaseElapsedSec];
        next[s.currentPhaseIdx] = (next[s.currentPhaseIdx] ?? 0) + live;
        set({
          phaseElapsedSec: next,
          paused: true,
          pausedAt: null,
          // Land on the RATING step first — barber's judgment is freshest
          // immediately after the cut, so we catch the quality signal
          // when attention is highest. Rating step continues to
          // after-photo (or summary if skipped); summary still surfaces
          // QualityPicker as a final correction step seeded from
          // pendingQuality.
          mode: 'rating',
          endedAtIso: new Date().toISOString(),
        });
      },

      // after-photo → reveal. Used when the barber taps "Show transformation"
      // (the primary CTA when an after photo/video has been captured). The
      // reveal step generates the transformation artifact, then offers
      // share / save / continue. Skipping this step (Skip & wrap-up) calls
      // goToSummaryFromAfter directly instead.
      goToRevealFromAfter: () => {
        set({ mode: 'reveal' });
      },

      // reveal → summary. Used by the "Continue to wrap-up" CTA on the
      // reveal screen. transformationGenerated remains true so the visit-
      // commit pass picks it up for the achievement.
      goToSummaryFromReveal: () => {
        set({ mode: 'summary' });
      },

      // after-photo → summary. Used by the "Skip & wrap-up" bypass CTA.
      // Captured photo (if any) stays in the store so PostCutSummary can
      // compose + upload it on save. The reveal step is bypassed; no
      // transformation generation runs, so transformationGenerated stays
      // false and the achievement isn't credited for this cut.
      goToSummaryFromAfter: () => {
        set({ mode: 'summary' });
      },

      closeModal: () => {
        const s = get();
        // If running OR mid-post-cut (after-photo / reveal), just minimize —
        // the cut isn't saved yet so we keep state in memory. Re-opening
        // lands on the chair view; the resume banner picks back up at the
        // running mode (or after-photo if endedAtIso is already set).
        // Note: photo and video blobs aren't persisted to localStorage,
        // so a full page reload loses them — the barber would just skip
        // the photo step on resume.
        if (s.mode === 'running' || s.mode === 'rating' || s.mode === 'after-photo' || s.mode === 'reveal') {
          set({ mode: 'idle' });
        } else if (s.mode === 'summary') {
          // Bailing out of summary without saving = discard the cut state.
          // Log as abandon — the timer was started + the user got all the
          // way to summary without saving, the strongest single friction
          // signal in the flow (high effort spent, no payoff).
          void logCutTimerAbandon({
            phase: 'summary',
            clientId: clientIdFor(s.client),
            cutTypeName: s.cutType?.name ?? null,
            startedAt: s.startedAtIso,
            timerStarted: true,
          });
          revokePhotoUrls(s);
          set({ mode: 'idle', ...freshActiveState() });
        } else {
          // chair / checkin / picker / before-photo — clear any pending state
          // so the next entry starts fresh. Log abandon ONLY when there's
          // actual pending state (a half-filled form). The naked 'chair'
          // mode with no client picked is just "user opened then closed
          // the modal" — not a meaningful abandon signal.
          const hasPending =
            s.pendingClient !== null ||
            s.pendingCutTypeId !== null ||
            s.cutType !== null;
          if (hasPending) {
            void logCutTimerAbandon({
              phase: s.mode,
              clientId: clientIdFor(s.pendingClient ?? s.client),
              cutTypeName: s.cutType?.name ?? null,
              startedAt: null,
              timerStarted: false,
            });
          }
          revokePhotoUrls(s);
          set({ mode: 'idle', ...freshActiveState() });
        }
      },

      resetAll: () => {
        revokePhotoUrls(get());
        set({ mode: 'idle', ...freshActiveState() });
      },

      tick: () => set((s) => ({ tickCount: s.tickCount + 1 })),
    }),
    {
      name: 'busy_cut_timer',
      // Only persist enough to resume an active cut. The transient picker/checkin states
      // shouldn't survive a refresh — it's confusing to come back and find a half-filled form.
      partialize: (s) => {
        // Only persist the running cut state. The chair / checkin / picker stages are
        // intentionally NOT persisted — finding a half-filled form after a refresh is
        // confusing. Persist on running + summary so a refresh resumes correctly.
        if (s.mode !== 'running' && s.mode !== 'summary') return { mode: 'idle' as const };
        return {
          mode: s.mode,
          cutType: s.cutType,
          client: s.client,
          currentPhaseIdx: s.currentPhaseIdx,
          phaseElapsedSec: s.phaseElapsedSec,
          phaseStartedAt: s.phaseStartedAt,
          phaseAccumPausedMs: s.phaseAccumPausedMs,
          pausedAt: s.pausedAt,
          paused: s.paused,
          addedAddons: s.addedAddons,
          sessionId: s.sessionId,
          startedAtIso: s.startedAtIso,
          endedAtIso: s.endedAtIso,
        };
      },
    }
  )
);

/* ============================================================================
 * Pure compute helpers (used by selectors + the LiveTimer screen)
 * ============================================================================ */

export function computeLiveElapsedSec(s: {
  cutType: CutType | null;
  phaseStartedAt: number | null;
  phaseAccumPausedMs: number;
  paused: boolean;
  pausedAt: number | null;
}): number {
  if (!s.cutType || s.phaseStartedAt == null) return 0;
  const now = Date.now();
  const currentPauseMs = s.paused && s.pausedAt != null ? now - s.pausedAt : 0;
  const liveMs = now - s.phaseStartedAt - s.phaseAccumPausedMs - currentPauseMs;
  return Math.max(0, Math.floor(liveMs / 1000));
}

export function getPhaseElapsedSec(s: CutTimerState, idx: number): number {
  const committed = s.phaseElapsedSec[idx] ?? 0;
  if (idx === s.currentPhaseIdx && s.mode === 'running') {
    return committed + computeLiveElapsedSec(s);
  }
  return committed;
}

export function getTotalElapsedSec(s: CutTimerState): number {
  let total = 0;
  for (let i = 0; i < s.phaseElapsedSec.length; i++) {
    total += s.phaseElapsedSec[i] ?? 0;
  }
  if (s.mode === 'running') total += computeLiveElapsedSec(s);
  return total;
}
